import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { BaggageType as BaggageTypeEnum } from '@websky/graphql';
import { Baggage as BaggageType } from '../../../../../BaggageService/types';
import Baggage from '../../../../Baggage';
import { default as BaseButton } from '../../../../../BaseComponents/Button';
import { useTheme } from '../../../../../theme';
import cn from 'classnames';
import Money from '../../../../../Money';
import { Segment } from '../../../../types';
import { useIsMobile } from '../../../../../utils';

interface Props {
	services: BaggageType[][];
	selectedBaggageId?: string;
	setSelectedBaggageId?: (id: string) => void;
	segments?: Segment[];
	allSegments?: boolean;
	showBaggageSizeOnMobile?: boolean;
}

const MobileBaggageSelector: React.FC<Props> = props => {
	const theme = useTheme('UpgradeBaggageDialog').MobileBaggageSelector;
	const { t } = useTranslation('BaggageService');

	const selectedService = React.useMemo(() => {
		return props.services.find(services => services[0].rfics === props.selectedBaggageId)?.[0];
	}, [props.services, props.selectedBaggageId]);

	const price = React.useMemo(() => {
		if (props.allSegments) {
			let amount = 0;

			props.segments.forEach(segment => {
				if (selectedService.segmentIds.some(segments => segments.includes(segment.id))) {
					amount += selectedService.price.amount;
				}
			});

			return {
				...selectedService.price,
				amount
			};
		}

		return selectedService.price;
	}, [selectedService, props.segments, props.allSegments]);

	const isMobile = useIsMobile();
	const isBaggageExcess = selectedService.type === BaggageTypeEnum.BaggageExcess;

	let showOnlyIcon = !isMobile || (isMobile && !isBaggageExcess);
	if (typeof props.showBaggageSizeOnMobile !== 'undefined') {
		showOnlyIcon = !props.showBaggageSizeOnMobile;
	}

	return (
		<div>
			{selectedService && <div className={theme.description}>{selectedService.name}</div>}

			{props.services.length === 1 && (
				<div
					className={cn(theme.container, theme.oneService, {
						[theme?.[props.services[0][0].type]]: theme?.[props.services[0][0].type]
					})}
					data-rfisc={props.services[0][0].rfics}
				>
					<Baggage baggage={props.services[0][0]} selected={false} onlyIcon={showOnlyIcon} short={true} />

					<div className={theme.description}>{props.services[0][0].size}</div>
				</div>
			)}

			{props.services.length > 1 && (
				<div className={theme.container} data-rfisc={props.services[0][0].rfics}>
					<Baggage
						baggage={props.services[0][0]}
						selected={false}
						short={true}
						iconClassName={theme.icon}
						onlyIcon={showOnlyIcon}
					/>

					{props.services.map(service => {
						return (
							<BaseButton
								variant="smallNeitral"
								className={cn(theme.selector, {
									[theme.selected]: service[0].rfics === props.selectedBaggageId
								})}
								onClick={() => props.setSelectedBaggageId(service[0].rfics)}
							>
								{service[0].value.amount} {service[0].value.measurement}
							</BaseButton>
						);
					})}
				</div>
			)}

			<div className={theme.footer}>
				<div className={theme.title_price}>
					{selectedService.value.amount} {t(selectedService.value.measurement)}
					{selectedService.description ? `, ${selectedService.description}` : ''}
				</div>
				<Money moneyClassName={theme.price} money={price} />
			</div>
		</div>
	);
};

export default MobileBaggageSelector;
